import { useEffect, useState } from 'react';
import api from '../lib/api';
import {
  Store, ExternalLink, CheckCircle2, XCircle, X, Plus, Trash2,
  FileText, Upload, Tag, Search, Building2, UploadCloud, Loader2,
} from 'lucide-react';
import { formatCaracasDateTime } from '../lib/timezone';

const STATUS_META = {
  PENDING:  { label: 'Pendiente', cls: 'stage-contactado' },
  APPROVED: { label: 'Aprobado',  cls: 'stage-cerrado_ganado' },
  REJECTED: { label: 'Rechazado', cls: 'stage-cerrado_perdido' },
};
const STATUS_TABS = ['PENDING', 'APPROVED', 'REJECTED', 'ALL'];
const TAB_LABELS = { PENDING: 'Pendientes', APPROVED: 'Aprobados', REJECTED: 'Rechazados', ALL: 'Todos' };
const REP_LABELS = { SHAREHOLDER: 'Accionista', MANAGER: 'Gerente' };
const CATEGORIES = ['Salud', 'Gastronomia', 'Moda', 'Tecnologia', 'Educacion', 'Hogar', 'Belleza', 'Automotriz', 'Entretenimiento', 'Otro'];

const toBase64 = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result);
  r.onerror = reject;
  r.readAsDataURL(file);
});

// Authenticated file-upload pill (reuses public Cloudinary stub endpoint).
function FileField({ label, testid, value, onUploaded }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setErr('');
    try {
      const b64 = await toBase64(file);
      const { data } = await api.post('/public/uploads', { file_base64: b64, filename: file.name });
      onUploaded(data.url);
    } catch {
      setErr('No se pudo subir');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="b2b-file">
      <span className="b2b-file-label">{label}</span>
      <label className={`b2b-file-btn ${value ? 'done' : ''}`}>
        {busy ? <Loader2 size={15} className="spin" /> : value ? <CheckCircle2 size={15} /> : <UploadCloud size={15} />}
        {busy ? 'Subiendo…' : value ? 'Cargado' : 'Seleccionar archivo'}
        <input data-testid={testid} type="file" hidden onChange={handle} accept="image/*,application/pdf" />
      </label>
      {err && <span className="b2b-file-err">{err}</span>}
    </div>
  );
}

// ── Create ally modal (internal operator flow) ──
function CreateAllyModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    company_name: '', rif: '', phone: '', address: '', category: 'Salud',
    mercantile_registry_url: '', logo_url: '',
  });
  const [reps, setReps] = useState([{ role: 'SHAREHOLDER', full_name: '', identity_card_url: '', rif_url: '' }]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const upd = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const updRep = (i, k, v) => setReps(prev => prev.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const addRep = () => setReps(prev => [...prev, { role: 'MANAGER', full_name: '', identity_card_url: '', rif_url: '' }]);
  const removeRep = (i) => setReps(prev => prev.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.company_name.trim() || !form.rif.trim()) {
      setError('Razón social y RIF son obligatorios'); return;
    }
    const validReps = reps.filter(r => r.full_name.trim());
    if (validReps.length === 0) {
      setError('Agrega al menos un representante con nombre'); return;
    }
    setSaving(true);
    try {
      await api.post('/admin/allies', { ...form, representatives: validReps });
      onCreated?.();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.detail || 'No se pudo crear el aliado');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose} data-testid="create-ally-modal">
      <div className="modal-content b2b-modal" onClick={e => e.stopPropagation()}>
        <div className="b2b-modal-head">
          <div className="b2b-modal-title">
            <div className="b2b-avatar" aria-hidden><Building2 size={20} /></div>
            <div><h2>Crear nuevo aliado</h2><div className="b2b-modal-sub"><span className="b2b-muted">Ingreso interno controlado</span></div></div>
          </div>
          <button className="close-btn" onClick={onClose} data-testid="close-create-ally"><X size={18} /></button>
        </div>

        <div className="b2b-modal-body">
          <form onSubmit={submit} className="b2b-form" id="create-ally-form">
            <h3 className="b2b-section">Datos de la empresa</h3>
            <div className="b2b-grid">
              <div className="form-group">
                <label>Razón social *</label>
                <input data-testid="new-company-name" value={form.company_name} onChange={upd('company_name')} required />
              </div>
              <div className="form-group">
                <label>RIF *</label>
                <input data-testid="new-company-rif" value={form.rif} onChange={upd('rif')} placeholder="J-12345678-9" required />
              </div>
              <div className="form-group">
                <label>Teléfono</label>
                <input data-testid="new-company-phone" value={form.phone} onChange={upd('phone')} />
              </div>
              <div className="form-group">
                <label>Rubro</label>
                <select data-testid="new-company-category" value={form.category} onChange={upd('category')}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="form-group b2b-col-2">
                <label>Dirección</label>
                <input data-testid="new-company-address" value={form.address} onChange={upd('address')} />
              </div>
            </div>

            <div className="b2b-grid">
              <FileField label="Registro Mercantil" testid="new-upload-mercantile" value={form.mercantile_registry_url} onUploaded={(url) => setForm(f => ({ ...f, mercantile_registry_url: url }))} />
              <FileField label="Logo de la empresa" testid="new-upload-logo" value={form.logo_url} onUploaded={(url) => setForm(f => ({ ...f, logo_url: url }))} />
            </div>

            <div className="b2b-section-row">
              <h3 className="b2b-section">Representantes</h3>
              <button type="button" className="b2b-btn-ghost" data-testid="new-add-rep" onClick={addRep}><Plus size={15} /> Agregar</button>
            </div>

            {reps.map((r, i) => (
              <div key={i} className="b2b-rep" data-testid={`new-rep-${i}`}>
                <div className="b2b-grid">
                  <div className="form-group">
                    <label>Tipo</label>
                    <select data-testid={`new-rep-role-${i}`} value={r.role} onChange={(e) => updRep(i, 'role', e.target.value)}>
                      <option value="SHAREHOLDER">Accionista</option>
                      <option value="MANAGER">Gerente / Administrador</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Nombre completo</label>
                    <input data-testid={`new-rep-name-${i}`} value={r.full_name} onChange={(e) => updRep(i, 'full_name', e.target.value)} />
                  </div>
                  <FileField label="Cédula de identidad" testid={`new-rep-ci-${i}`} value={r.identity_card_url} onUploaded={(url) => updRep(i, 'identity_card_url', url)} />
                  <FileField label="RIF del representante" testid={`new-rep-rif-${i}`} value={r.rif_url} onUploaded={(url) => updRep(i, 'rif_url', url)} />
                </div>
                {reps.length > 1 && (
                  <button type="button" className="b2b-rep-remove" data-testid={`new-remove-rep-${i}`} onClick={() => removeRep(i)}><Trash2 size={14} /></button>
                )}
              </div>
            ))}

            {error && <div className="form-error" data-testid="create-ally-error" style={{ marginTop: 12 }}>{error}</div>}
          </form>
        </div>

        <div className="b2b-modal-foot">
          <button className="btn-cancel" onClick={onClose} data-testid="cancel-create-ally">Cancelar</button>
          <button type="submit" form="create-ally-form" className="btn-primary" data-testid="submit-create-ally" disabled={saving}>
            {saving ? 'Guardando…' : 'Crear aliado'}
          </button>
        </div>
      </div>
    </div>
  );
}

function DocLink({ label, url, testid }) {
  if (!url) return <div className="b2b-doc b2b-doc-empty"><FileText size={14} /> {label}: <em>no adjunto</em></div>;
  return (
    <a className="b2b-doc" href={url} target="_blank" rel="noreferrer" data-testid={testid}>
      <FileText size={14} /> {label} <ExternalLink size={13} />
    </a>
  );
}

// ── Catalog manager (only for APPROVED allies) ──
function CatalogManager({ ally, onChanged }) {
  const [items, setItems] = useState(ally.catalog || []);
  const [form, setForm] = useState({ item_type: 'PRODUCT', item_name: '', discount_percentage: '' });
  const [error, setError] = useState('');
  const [importResult, setImportResult] = useState(null);

  const reload = async () => {
    const { data } = await api.get(`/admin/allies/${ally.id}/catalog`);
    setItems(data.items || []);
    onChanged?.();
  };

  const addItem = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await api.post(`/admin/allies/${ally.id}/catalog`, {
        item_type: form.item_type,
        item_name: form.item_name,
        discount_percentage: parseFloat(form.discount_percentage),
      });
      setForm({ item_type: 'PRODUCT', item_name: '', discount_percentage: '' });
      await reload();
    } catch (err) {
      setError(err?.response?.data?.detail || 'No se pudo agregar');
    }
  };

  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(''); setImportResult(null);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await api.post(`/admin/allies/${ally.id}/catalog/import`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(data);
      await reload();
    } catch (err) {
      setError(err?.response?.data?.detail || 'No se pudo importar');
    } finally {
      e.target.value = '';
    }
  };

  const delItem = async (id) => {
    await api.delete(`/admin/allies/${ally.id}/catalog/${id}`);
    await reload();
  };

  return (
    <div className="b2b-catalog" data-testid="catalog-manager">
      <h3 className="b2b-section"><Tag size={16} /> Catálogo de descuentos</h3>

      <form className="b2b-catalog-form" onSubmit={addItem}>
        <select data-testid="catalog-type" value={form.item_type} onChange={(e) => setForm(f => ({ ...f, item_type: e.target.value }))}>
          <option value="PRODUCT">Producto</option>
          <option value="SERVICE">Servicio</option>
        </select>
        <input data-testid="catalog-name" placeholder="Nombre del producto / servicio" value={form.item_name} onChange={(e) => setForm(f => ({ ...f, item_name: e.target.value }))} required />
        <input data-testid="catalog-discount" type="number" min="0" max="100" step="0.5" placeholder="% desc." value={form.discount_percentage} onChange={(e) => setForm(f => ({ ...f, discount_percentage: e.target.value }))} required />
        <button type="submit" className="btn-primary" data-testid="catalog-add"><Plus size={15} /> Agregar</button>
        <label className="b2b-btn-ghost b2b-import" data-testid="catalog-import-label">
          <Upload size={15} /> Importar CSV/Excel
          <input data-testid="catalog-import" type="file" hidden accept=".csv,.xlsx,.xls" onChange={importCsv} />
        </label>
      </form>
      <div className="b2b-muted" style={{ fontSize: 12 }}>Columnas esperadas: <code>item_type, item_name, discount_percentage</code></div>

      {importResult && (
        <div className="b2b-import-result" data-testid="import-result">
          Importados: <strong>{importResult.inserted}</strong> de {importResult.total_rows}.
          {importResult.errors?.length > 0 && <span> {importResult.errors.length} fila(s) con error.</span>}
        </div>
      )}
      {error && <div className="form-error" style={{ marginTop: 8 }}>{error}</div>}

      <table className="contacts-table" data-testid="catalog-table" style={{ marginTop: 12 }}>
        <thead><tr><th>Tipo</th><th>Nombre</th><th>Descuento</th><th></th></tr></thead>
        <tbody>
          {items.length === 0 && <tr><td colSpan={4} className="b2b-muted" style={{ padding: 16 }}>Sin items aún.</td></tr>}
          {items.map(it => (
            <tr key={it.id} data-testid={`catalog-row-${it.id}`}>
              <td>{it.item_type === 'PRODUCT' ? 'Producto' : 'Servicio'}</td>
              <td>{it.item_name}</td>
              <td><strong>{it.discount_percentage}%</strong></td>
              <td>
                <button className="btn-icon" data-testid={`catalog-del-${it.id}`} title="Eliminar" onClick={() => delItem(it.id)}>
                  <Trash2 size={15} color="#dc2626" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Detail modal ──
function AllyDetailModal({ allyId, onClose, onUpdated }) {
  const [ally, setAlly] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/allies/${allyId}`);
      setAlly(data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'No se pudo cargar');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [allyId]);

  const approve = async () => {
    const { data } = await api.post(`/admin/allies/${allyId}/approve`);
    setAlly(a => ({ ...a, ...data })); onUpdated?.();
  };
  const reject = async () => {
    const reason = window.prompt('Motivo del rechazo (opcional):') || '';
    const { data } = await api.post(`/admin/allies/${allyId}/reject`, { reason });
    setAlly(a => ({ ...a, ...data })); onUpdated?.();
  };

  const st = ally ? (STATUS_META[ally.status] || STATUS_META.PENDING) : null;
  const initials = (ally?.company_name || '?').trim().slice(0, 2).toUpperCase();

  return (
    <div className="modal-overlay" onClick={onClose} data-testid="ally-detail-modal">
      <div className="modal-content b2b-modal" onClick={e => e.stopPropagation()}>
        <div className="b2b-modal-head">
          <div className="b2b-modal-title">
            <div className="b2b-avatar" aria-hidden>
              {ally?.logo_url ? <img src={ally.logo_url} alt="" onError={(e) => { e.target.style.display = 'none'; }} /> : initials}
            </div>
            <div>
              <h2>{ally?.company_name || 'Aliado'}</h2>
              {ally && (
                <div className="b2b-modal-sub">
                  <span data-testid="ally-status-badge" className={`stage-badge ${st.cls}`}>{st.label}</span>
                  {ally.reviewed_by && <span className="b2b-muted">Revisado por {ally.reviewed_by}</span>}
                </div>
              )}
            </div>
          </div>
          <button className="close-btn" onClick={onClose} data-testid="close-ally-modal"><X size={18} /></button>
        </div>

        <div className="b2b-modal-body">
          {loading ? <div className="text-muted" style={{ padding: 8 }}>Cargando…</div> : error ? <div className="form-error">{error}</div> : ally && (
            <div className="b2b-detail">
              <div className="b2b-grid">
                <div><span className="b2b-k">RIF</span><div>{ally.rif}</div></div>
                <div><span className="b2b-k">Rubro</span><div>{ally.category || '—'}</div></div>
                <div><span className="b2b-k">Teléfono</span><div>{ally.phone || '—'}</div></div>
                <div><span className="b2b-k">Registrado</span><div>{ally.created_at ? formatCaracasDateTime(ally.created_at) : '—'}</div></div>
                <div className="b2b-col-2"><span className="b2b-k">Dirección</span><div>{ally.address || '—'}</div></div>
              </div>

              <h3 className="b2b-section"><FileText size={16} /> Documentos legales</h3>
              <div className="b2b-docs">
                <DocLink label="Registro Mercantil" url={ally.mercantile_registry_url} testid="doc-mercantile" />
                <DocLink label="Logo" url={ally.logo_url} testid="doc-logo" />
              </div>

              <h3 className="b2b-section">Representantes</h3>
              <div className="b2b-reps">
                {(ally.representatives || []).map(r => (
                  <div key={r.id} className="b2b-rep-card" data-testid={`rep-card-${r.id}`}>
                    <div className="b2b-rep-name">{r.full_name} <span className="b2b-rep-role">{REP_LABELS[r.role] || r.role}</span></div>
                    <div className="b2b-docs">
                      <DocLink label="Cédula" url={r.identity_card_url} testid={`doc-ci-${r.id}`} />
                      <DocLink label="RIF" url={r.rif_url} testid={`doc-rif-${r.id}`} />
                    </div>
                  </div>
                ))}
                {(ally.representatives || []).length === 0 && <div className="b2b-muted">Sin representantes.</div>}
              </div>

              {ally.status === 'REJECTED' && ally.rejection_reason && (
                <div className="b2b-reject-reason" data-testid="reject-reason">Motivo: {ally.rejection_reason}</div>
              )}

              {ally.status === 'APPROVED' && <CatalogManager ally={ally} onChanged={load} />}
            </div>
          )}
        </div>

        {ally && ally.status === 'PENDING' && (
          <div className="b2b-modal-foot">
            <button className="btn-cancel b2b-reject" data-testid="reject-ally" onClick={reject}><XCircle size={16} /> Rechazar</button>
            <button className="btn-primary b2b-approve" data-testid="approve-ally" onClick={approve}><CheckCircle2 size={16} /> Aprobar</button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function AlliesInboxPage() {
  const [allies, setAllies] = useState([]);
  const [tab, setTab] = useState('PENDING');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState(null);
  const [query, setQuery] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  const load = async (status = tab) => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/allies', { params: { status } });
      setAllies(data.allies || []);
    } catch (err) {
      setError(err?.response?.data?.detail || 'No se pudieron cargar los aliados');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(tab); /* eslint-disable-next-line */ }, [tab]);

  const q = query.trim().toLowerCase();
  const filtered = q
    ? allies.filter(a =>
        (a.company_name || '').toLowerCase().includes(q) ||
        (a.rif || '').toLowerCase().includes(q))
    : allies;

  return (
    <div className="contacts-page" data-testid="allies-inbox-page">
      <div className="contacts-toolbar b2b-toolbar">
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, margin: 0 }}>
          <Store size={18} /> Aliados Comerciales
        </h3>
        <div className="b2b-toolbar-right">
          <button className="btn-primary" data-testid="create-ally-btn" onClick={() => setShowCreate(true)} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Plus size={16} /> Crear nuevo aliado
          </button>
          <div className="b2b-search">
            <Search size={15} />
            <input
              data-testid="ally-search-input"
              placeholder="Buscar por empresa o RIF…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            {query && <button className="b2b-search-clear" data-testid="ally-search-clear" onClick={() => setQuery('')} aria-label="Limpiar"><X size={14} /></button>}
          </div>
          <div className="b2b-tabs">
            {STATUS_TABS.map(t => (
              <button key={t} data-testid={`tab-${t}`} className={`b2b-tab ${tab === t ? 'active' : ''}`} onClick={() => setTab(t)}>
                {TAB_LABELS[t]}
              </button>
            ))}
          </div>
        </div>
      </div>

      {error && <div className="form-error" style={{ marginTop: 12 }}>{error}</div>}

      <div className="contacts-table-wrapper" style={{ marginTop: 16 }}>
        {loading ? <div className="text-muted" style={{ padding: 16 }}>Cargando…</div> : (
          <table className="contacts-table" data-testid="allies-table">
            <thead>
              <tr><th>Empresa</th><th>RIF</th><th>Rubro</th><th>Reps</th><th>Catálogo</th><th>Máx. desc.</th><th>Estado</th><th>Registrado</th></tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={8} className="b2b-muted" style={{ padding: 16 }}>
                  {q ? `Sin resultados para “${query}”.` : 'No hay aliados en esta vista.'}
                </td></tr>
              )}
              {filtered.map(a => {
                const st = STATUS_META[a.status] || STATUS_META.PENDING;
                return (
                  <tr key={a.id} data-testid={`ally-row-${a.id}`} className="b2b-clickable" onClick={() => setOpenId(a.id)}>
                    <td className="td-name">{a.company_name}</td>
                    <td>{a.rif}</td>
                    <td>{a.category || '—'}</td>
                    <td>{a.rep_count ?? 0}</td>
                    <td>{a.catalog_count ?? 0}</td>
                    <td>{a.max_discount ? `${a.max_discount}%` : '—'}</td>
                    <td><span className={`stage-badge ${st.cls}`}>{st.label}</span></td>
                    <td>{a.created_at ? formatCaracasDateTime(a.created_at) : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {openId && (
        <AllyDetailModal allyId={openId} onClose={() => setOpenId(null)} onUpdated={() => load(tab)} />
      )}

      {showCreate && (
        <CreateAllyModal onClose={() => setShowCreate(false)} onCreated={() => load(tab)} />
      )}
    </div>
  );
}
